import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Network } from "lucide-react";
import {
  adjacentExperience,
  getExperience,
  getIndustry,
  getProjectsForExperience,
  yearRange,
} from "../lib/queries";
import { BrandLogo } from "../lib/brands";
import { BookmarkButton } from "../components/BookmarkButton";
import { TalkToMeButton } from "../components/ChatPanel";
import {
  AskAbout,
  BackBar,
  IndustryChip,
  NotFound,
  SkillChip,
  TagRow,
  TechChip,
  paragraphs,
} from "../components/DetailBits";
import { authEnabled } from "../auth/config";
import { AdminEditExperience } from "../components/ExperienceEditor";
import { ShareThoughts } from "../components/ShareThoughts";
import { useOverlayVersion } from "../lib/overlay";

export function Experience() {
  const { id } = useParams<{ id: string }>();
  useOverlayVersion(); // re-render when an admin edit lands or admin-mode toggles
  const e = id ? getExperience(id) : undefined;
  if (!e) return <NotFound />;

  const present = e.end === "present";
  const projects = getProjectsForExperience(e.id);
  const { prev, next } = adjacentExperience(e.id);

  return (
    <article className="detail wrap-narrow section">
      <BackBar to="/" label="All experience" />
      <header className="detail-head">
        <BrandLogo company={e.company} size={64} />
        <div className="grow">
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <span className="eyebrow">Experience</span>
            {present && <span className="now-tag mono">Now</span>}
          </div>
          <div className="title-row">
            <h1 className="display detail-title">{e.company}</h1>
            <BookmarkButton nodeType="experience" nodeId={e.id} title={e.company} />
          </div>
          <div className="detail-role">{e.role}</div>
          <div className="detail-dates mono">{yearRange(e.start, e.end)}</div>
        </div>
      </header>

      {authEnabled && <AdminEditExperience key={e.id} experience={e} />}

      <div className="detail-actions">
        <AskAbout prompt={`Tell me about Srinath's work at ${e.company}.`} />
        <TalkToMeButton
          context={{
            nodeType: "experience",
            nodeId: e.id,
            nodeTitle: `${e.company} — ${e.role}`,
            nodeSummary: e.summary,
          }}
        />
        <Link to={`/?cview=graph&gfocus=experience:${e.id}`} className="btn btn-sm">
          <Network size={15} /> See in graph
        </Link>
      </div>

      <div className="detail-body">{paragraphs(e.narrative || e.summary)}</div>

      <div className="detail-tags">
        <TagRow label="Industries">
          {e.industries.map((i) => (
            <IndustryChip key={i} id={i} />
          ))}
        </TagRow>
        <TagRow label="Technologies">
          {e.technologies.map((tx) => (
            <TechChip key={tx} id={tx} />
          ))}
        </TagRow>
        <TagRow label="Skills">
          {e.skills.map((s) => (
            <SkillChip key={s} id={s} />
          ))}
        </TagRow>
      </div>

      {projects.length > 0 && (
        <section className="detail-section">
          <div className="section-head">
            <h2 className="display" style={{ fontSize: 26 }}>
              Signature work
            </h2>
            <span className="mono muted">
              {projects.length} engagement{projects.length === 1 ? "" : "s"}
            </span>
          </div>
          <ul className="proj-list">
            {projects.map((p) => (
              <li key={p.id}>
                <Link to={`/project/${p.id}`} className="proj-row card card-link card-pad">
                  <div className="grow">
                    <div className="proj-row-title">{p.title}</div>
                    <p className="proj-row-sum">{p.summary}</p>
                    <div className="wrapflex" style={{ gap: 6, marginTop: 10 }}>
                      {p.industries.slice(0, 3).map((i) => (
                        <span key={i} className={`mini-tag hue-${i}`}>
                          <span className="dot" style={{ background: "var(--hue)" }} />
                          {getIndustry(i)?.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="proj-row-arrow" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(prev || next) && (
        <nav className="detail-pager" aria-label="Experience pagination">
          {prev ? (
            <Link to={`/experience/${prev.id}`} className="detail-pager-link">
              <ArrowLeft size={16} />
              <span className="detail-pager-text">
                <span className="detail-pager-kicker mono">Previous experience</span>
                <span className="detail-pager-name">{prev.company}</span>
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/experience/${next.id}`} className="detail-pager-link detail-pager-next">
              <span className="detail-pager-text">
                <span className="detail-pager-kicker mono">Next experience</span>
                <span className="detail-pager-name">{next.company}</span>
              </span>
              <ArrowRight size={16} />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}
      <ShareThoughts className="page-share-bottom" />
    </article>
  );
}
